/* eslint-disable no-param-reassign */
import moment from 'moment-timezone';
import {
  NumericDataTypes,
  defaultChartDimensions, defaultKPIDimensions, defaultRowHeight, defaultTableDimensions,
} from 'core/constants/dashboard';
import { VisualizationTypes } from 'core/constants/visualizations';
import {
  IDashboardVisualization, IFilterItem, IFilterLinkInfo, IPanel, IReportMappedFilters,
} from 'core/models/dashboard';
import { Obj } from 'core/models/obj';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { IColumn, IFilterConfig, IFiltersMetaData } from 'core/models/report-response';
import { findWrapperByConstituentIds } from 'core/utils/common.utility';
import { ObjModel } from 'core/models';
import { FilterType, LinkedFilterSource } from 'core/constants/filter';
import { isValidArray, isValidArrayAndNotEmpty, isValidObjectAndNotEmpty } from 'components/feature/Report/ReportSidebar/common/helpers';
import { ColumnDataTypes } from 'core/constants/report';
import { IDateRangeTypes } from 'core/constants/date';
import isString from 'lodash/isString';
import { ShowErrorForWidget } from 'components/feature/Report/ReportSidebar/common/constants';
import { SectionItemId } from 'core/constants/report-listing';
import { DashboardMobileStandardViewParam, MobileHomePageURLParam, MobileListingURLParam } from 'core/constants/common';
import { isDimension, isMeasure, isMeasureDynamic } from './report.util';
import { getFormattedValues } from './formatters.utility';
import { getDatesIfRelativeRangeIsRestricted, getDatesIfAbsoluteRangeIsRestricted } from './date.util';

export const getDashboardLayoutDetails = ({
  Margin, IsDraggable, IsResizable, CompactType,
}: IDashboardVisualization) => ({
  margin: Margin,
  isDraggable: IsDraggable,
  isResizable: IsResizable,
  rowHeight: defaultRowHeight,
  compactType: CompactType,
});

export const getDefaultDimensions = (typeOfPanel: VisualizationTypes) => {
  switch (typeOfPanel) {
    case VisualizationTypes.KPI:
      return defaultKPIDimensions;
    case VisualizationTypes.Table:
      return defaultTableDimensions;
    case VisualizationTypes.BarChart:
    case VisualizationTypes.ColumnChart:
    case VisualizationTypes.LineChart:
    case VisualizationTypes.AreaChart:
    case VisualizationTypes.PieChart:
    case VisualizationTypes.DonutChart:
    case VisualizationTypes.EmbeddedUrl:
    default:
      return defaultChartDimensions;
  }
};

export const getPanelWidth = (currentWidth: number, panelType: VisualizationTypes) => {
  const defaultDimensions = getDefaultDimensions(panelType);
  let resWidth = currentWidth;
  if (resWidth < defaultDimensions.minW) {
    resWidth = defaultDimensions.minW;
  }
  if (resWidth > defaultDimensions.maxW) {
    resWidth = defaultDimensions.maxW;
  }
  return resWidth;
};

export const getPanelHeight = (currentHeight: number, panelType: VisualizationTypes) => {
  const defaultDimensions = getDefaultDimensions(panelType);
  let resHeight = currentHeight;
  if (resHeight < defaultDimensions.minH) {
    resHeight = defaultDimensions.minH;
  }
  if (resHeight > defaultDimensions.maxH) {
    resHeight = defaultDimensions.maxH;
  }
  return resHeight;
};

export const isColumnsEmpty = (panelCols:string[]) => (
  panelCols === null || panelCols?.length === 0
);

export const getEnumKey = (obj: any, value: any) => {
  const keyIndex = Object.values(obj).indexOf(value);
  return Object.keys(obj)[keyIndex];
};

export const getGlobalFilter = (globalFilters: Obj, panel: IPanel, wrapperPool?: IFilterConfig[]) => {
  const globalFiltersPayload:Obj = {};
  if (globalFilters) {
    Object?.entries(globalFilters).forEach((item) => {
      const mappedId = panel?.Source?.GlobalFiltersMapping?.[item[0] as keyof object];
      if (!mappedId) return;
      // mappedId is string[] when the linked target is a custom-filter wrapper —
      // fan the dashboard value out to each constituent ID. Per-constituent
      // envelope translation (User/LOS/Number) is handled at the panel-fetch
      // dispatch site, not here.
      if (isValidArray(mappedId)) {
        (mappedId as string[]).forEach((id) => {
          globalFiltersPayload[id] = item[1];
        });
        return;
      }
      // Single-string mapping may still hold a wrapper ID — either because the
      // dashboard was saved before the save-time fan-out landed, or because the
      // mapping came back from the BE in legacy shape. Look up the wrapper in
      // the report's filter pool and fan out at payload-build time.
      const id = mappedId as string;
      const wrapper = wrapperPool?.find((f) => f.ID === id && f.IsCustomFilter);
      if (wrapper && isValidArrayAndNotEmpty(wrapper.LinkedFilters)) {
        wrapper.LinkedFilters.forEach((lf) => {
          const key = lf.Source === LinkedFilterSource.SubBuilder && lf.QueryId
            ? `${lf.FilterId}_${lf.QueryId}`
            : lf.FilterId;
          globalFiltersPayload[key] = item[1];
        });
        return;
      }
      globalFiltersPayload[id] = item[1];
    });
  }

  return globalFiltersPayload;
};

export const getFiltersPayload = (filters:IFilterItem[]) => {
  const globalFiltersObj:Obj = {};
  if (isValidArrayAndNotEmpty(filters)) {
    filters?.forEach((filter:IFilterItem) => {
      if (filter.Type === FilterType.UserMultiSelect) {
        globalFiltersObj[filter.ID] = filter?.filterData?.FilterResponse?.UserIds;
      } else if (filter.Type === FilterType.DateRange) {
        globalFiltersObj[filter.ID] = filter?.filterData?.FilterResponse;
      } else {
        const filterReponse = filter?.filterData?.FilterResponse;
        globalFiltersObj[filter.ID] = filterReponse;
      }
    });
  }
  return globalFiltersObj;
};

export const isPanelsFiltered = (dynamicGlobalFilters: IFilterItem[], staticGlobalFilters: IFilterItem[], globalFilter: Object) : boolean => {
  let value = false;

  if (isEqual(dynamicGlobalFilters, staticGlobalFilters)) {
    return false;
  }
  if (globalFilter) {
    const MappedFilters = Object.keys(globalFilter);

    dynamicGlobalFilters.forEach((item, index) => {
      if (MappedFilters.includes(item.ID) && !isEqual(dynamicGlobalFilters[index]?.filterData.FilterResponse, staticGlobalFilters[index]?.filterData.FilterResponse)) {
        value = true;
      }
    });
  }

  return value;
};

export const filterAppliedOnPanels = (staticGlobalFilters: IFilterItem[], dashboardConfigFilters: IFilterItem[], globalFilter: Object) => {
  const filtersApplied : string[] = [];
  if (isEqual(staticGlobalFilters, dashboardConfigFilters)) {
    return filtersApplied;
  }
  if (dashboardConfigFilters.length > staticGlobalFilters.length) {
    filtersApplied.push(dashboardConfigFilters.find((filter) => !staticGlobalFilters.includes(filter))?.Label);
  }
  if (globalFilter) {
    const MappedFilters = Object.keys(globalFilter);

    staticGlobalFilters.forEach((item, index) => {
      if (MappedFilters.includes(item.ID) && !isEqual(staticGlobalFilters[index]?.filterData?.FilterResponse, dashboardConfigFilters[index]?.filterData?.FilterResponse)) {
        filtersApplied.push(item.Label);
      }
    });
  }

  return filtersApplied;
};

export const filterAppliedOnDashboardBuilder = (dynamicGlobalFilters: IFilterItem[], globalFilter: Object) => {
  const filtersApplied : string[] = [];
  if (globalFilter) {
    const MappedFilters = Object.keys(globalFilter);

    dynamicGlobalFilters.forEach((item) => {
      if (MappedFilters.includes(item.ID)) {
        filtersApplied.push(item.Label);
      }
    });
  }

  return filtersApplied;
};
export const getPanelConfigMappedColumns = (panelFetchReportColumns:IColumn[], panelConfigColumns: Array<string | ObjModel.Obj>, isReportTypeList?: boolean) => {
  let panelConfigMappedColumns:IColumn[] = [];
  let dimensions:IColumn[] = [];
  if (!isReportTypeList) {
    dimensions = panelFetchReportColumns?.filter((colObj: IColumn) => !isMeasure(colObj));
  }
  let measures:IColumn[] = [];
  panelConfigColumns?.forEach((panelCol:string | ObjModel.Obj) => {
    const panelColObj = parseAndGetColumnObj(panelFetchReportColumns, panelCol);
    if ((isReportTypeList && (isDimension(panelColObj) || panelColObj.Props?.ParentDimension))
      || isMeasure(panelColObj)
      || panelColObj?.Id === ShowErrorForWidget) {
      const isExists = measures?.find((measure) => panelColObj?.Id === measure.Id);
      if (!isExists) {
        measures.push(panelColObj);
      }
    }
  });
  if (measures.some((m) => m.Id !== ShowErrorForWidget)) {
    measures = measures.filter((m) => m.Id !== ShowErrorForWidget);
  }
  panelConfigMappedColumns = dimensions.concat(measures);
  return panelConfigMappedColumns;
};

// FE-to-FE variant of getLinkedFilters for the save-temp-view / save-temp-drilldown
// path. The temp record is consumed by the report page on the next tab, whose
// filter store is keyed by wrapper IDs for custom filters (constituents have no
// appliedFilters entry of their own). So instead of fanning a wrapper pick out
// to constituent payload keys, resolve back to the wrapper ID — the report's
// load-time write-through then applies the value to appliedFilters[wrapperId]
// and the wrapper UI displays the dashboard's selection from first render.
// See getLinkedFilters below for the BE-facing (fetch-report-data) variant.
export const getLinkedFiltersForReportLoad = (
  GlobalFiltersMapping: Object,
  globalFiltersValue: ObjModel.Obj,
  wrapperPool?: IFilterConfig[],
) => {
  if (isEmpty(GlobalFiltersMapping) || isEmpty(globalFiltersValue)) return null;
  const LinkedFilters: ObjModel.Obj = {};
  Object.keys(GlobalFiltersMapping).forEach((dashFilterId) => {
    const mappedId = GlobalFiltersMapping[dashFilterId as keyof object];
    if (mappedId === undefined || mappedId === null) return;
    const value = globalFiltersValue[dashFilterId];
    if (value === undefined) return;
    // Wrapper pick (post-fan-out array shape) — resolve back to the wrapper ID
    // so the report page's appliedFilters[wrapperId] gets the dashboard value.
    if (isValidArray(mappedId)) {
      const wrapper = findWrapperByConstituentIds(wrapperPool || [], mappedId as unknown as string[]);
      if (wrapper?.ID) LinkedFilters[wrapper.ID] = value;
      return;
    }
    // Legacy single-string wrapper ID or plain non-wrapper filter ID — both
    // pass through as-is. The report page recognizes either.
    LinkedFilters[mappedId as string] = value;
  });
  return LinkedFilters;
};

export const getLinkedFilters = (
  GlobalFiltersMapping: Object,
  globalFiltersValue: ObjModel.Obj,
  wrapperPool?: IFilterConfig[],
) => {
  if (isEmpty(GlobalFiltersMapping) || isEmpty(globalFiltersValue)) return null;
  const LinkedFilters: ObjModel.Obj = {};
  const globalFiltersID = Object.keys(GlobalFiltersMapping);

  globalFiltersID.forEach((filterID) => {
    const mappedId = GlobalFiltersMapping[filterID as keyof object];
    if (mappedId === undefined || mappedId === null) return;
    // Array-shape mapping (wrapper expanded at save time) — fan the dashboard
    // value out to each constituent ID.
    if (isValidArray(mappedId)) {
      (mappedId as unknown as string[]).forEach((id) => {
        LinkedFilters[id] = globalFiltersValue[filterID];
      });
      return;
    }
    // Legacy single-string mapping may still hold a wrapper ID — fan out at
    // payload-build time when the wrapper is resolvable in the panel's report
    // filter pool. Mirrors getGlobalFilter.
    const id = mappedId as string;
    const wrapper = wrapperPool?.find((f) => f.ID === id && f.IsCustomFilter);
    if (wrapper && isValidArrayAndNotEmpty(wrapper.LinkedFilters)) {
      wrapper.LinkedFilters.forEach((lf) => {
        const key = lf.Source === LinkedFilterSource.SubBuilder && lf.QueryId
          ? `${lf.FilterId}_${lf.QueryId}`
          : lf.FilterId;
        LinkedFilters[key] = globalFiltersValue[filterID];
      });
      return;
    }
    LinkedFilters[id] = globalFiltersValue[filterID];
  });

  return LinkedFilters;
};

// Fans wrapper-keyed entries in `inputFilters` out into their constituent payload
// keys for the dashboard-drilldown report-load fetch (getFetchDrilldownReportV2).
// The dashboard ships filters keyed by the wrapper ID, but the BE bake
// (overwriteGlobalFilters) only overwrites fetchCriteria.Filters keys (Render.ID —
// bare for main builder, <filterId>_<queryId> for sub-builder).
//
// The dashboard value is already the RAW per-constituent value (see getFiltersPayload:
// User → bare UserIds array, others → FilterResponse), exactly what the BE fetch
// expects per constituent — the same value getLinkedFilters fans out for the
// dashboard's own panel fetch. So we write it to each constituent key AS-IS; no
// envelope translation. The User select-type rides separately in FiltersMetadata
// (getLinkedUserFilterType), which flows through untouched on the request. Non-wrapper
// keys pass through unchanged.
export const expandWrapperFilters = (
  inputFilters: ObjModel.Obj,
  filterConfig: IFilterConfig[],
): ObjModel.Obj => {
  const expanded: ObjModel.Obj = {};
  if (isEmpty(inputFilters)) return expanded;
  Object.keys(inputFilters).forEach((key) => {
    const value = inputFilters[key];
    const wrapper = (filterConfig || []).find((f) => f.ID === key && f.IsCustomFilter);
    if (wrapper && isValidArrayAndNotEmpty(wrapper.LinkedFilters)) {
      wrapper.LinkedFilters.forEach((lf) => {
        const payloadKey = lf.Source === LinkedFilterSource.SubBuilder && lf.QueryId
          ? `${lf.FilterId}_${lf.QueryId}`
          : lf.FilterId;
        expanded[payloadKey] = value;
      });
      return;
    }
    expanded[key] = value;
  });
  return expanded;
};

export const isMailMergeURL = (url: string) => url.includes('@{User:SecretKey ,}') || url.includes('@{User:AccessKey ,}');

export const getKPIValue = (grandTotals:Object, columns:IColumn[], selctedMeasureId: string, userTimeZone: any) => {
  const selectedColumn = columns?.find((col) => col?.Id === selctedMeasureId);
  const formatter = selectedColumn?.Props?.Formatter;
  const renderType = isMeasure(selectedColumn) ? selectedColumn?.Props?.Measure?.RenderType : selectedColumn?.BuilderConfig?.RenderType;
  let value: any = 0;
  if (grandTotals && selctedMeasureId in grandTotals) {
    value = grandTotals[selctedMeasureId as keyof typeof grandTotals];
    value = getFormattedValues(value, formatter, userTimeZone, false, renderType) || 0;
  }
  return value;
};
// Utilising filterId to fetch the next adjecent selected filterObj
// @linkedPanelFilter: object where panelId is the key and filterId is the value
// @panel: id of the selected panel
// @panelList: list of available panels
// @reportMappedFilters: object where reportId is the key and list of corresponding filters is the value
// @filterData: selectedFilter object
export const getFilterInfo = (linkedPanelFilter:IFilterLinkInfo, panelID:string, panelList:IPanel[], reportMappedFilters:IReportMappedFilters, filterData:IFilterItem) :IFilterItem => {
  const filterInfo = {
    ...filterData,
  };
  const remainingLinkedPanelIdsList = Object.keys(linkedPanelFilter).filter((item) => item !== panelID);
  const filterIdOfChosenPanel = isValidArray(remainingLinkedPanelIdsList) && remainingLinkedPanelIdsList.length > 0 ? linkedPanelFilter[remainingLinkedPanelIdsList[0]] : null;

  if (filterIdOfChosenPanel) {
    const reportId = panelList.find((item:IPanel) => item.Id === remainingLinkedPanelIdsList[0])?.Source?.SourceId;
    // Wrapper-linked panels persist an array of constituent IDs — resolve back
    // to the wrapper row in the report's filter pool so the schema (FilterKey /
    // DataType) below comes from the wrapper itself.
    const reportPool = reportId && isValidObjectAndNotEmpty(reportMappedFilters)
      ? Object.values(reportMappedFilters[reportId as keyof Object]) as IFilterConfig[]
      : [];
    let filterItems: IFilterConfig[] = [];
    if (isValidArray(filterIdOfChosenPanel)) {
      const wrapper = findWrapperByConstituentIds(reportPool, filterIdOfChosenPanel as unknown as string[]);
      if (wrapper) filterItems = [wrapper];
    } else if (isValidObjectAndNotEmpty(reportMappedFilters)) {
      filterItems = Object.values(reportMappedFilters[reportId as keyof Object]).filter((filter:IFilterItem) => filter?.ID === filterIdOfChosenPanel) as IFilterConfig[];
    }
    const isValidFilterList = isValidArray(filterItems) && filterItems.length > 0;
    filterInfo.Metadata = {
      FilterKey: isValidFilterList ? filterItems[0]?.Metadata?.FilterKey : '',
      DataSourcePanelId: remainingLinkedPanelIdsList[0],
    };
    filterInfo.DataType = isValidFilterList ? filterItems[0]?.DataType : ColumnDataTypes.Default;
    filterInfo.IsLargeOptionSetField = isValidFilterList ? filterItems[0]?.IsLargeOptionSetField : false;
  } else {
    filterInfo.Metadata = {};
  }

  if (!isValidObjectAndNotEmpty(filterInfo?.linkedPanelFilter)) {
    filterInfo.DataType = undefined;
  }
  return filterInfo;
};

// Get the FilterMetaInfo for UserMultiSelect Type filter for linked panel
// @GlobalFiltersMapping: object where panelId is the key and filterId is the value
// @globalFiltersValue: object where global filter is the key and linked report filter is the value
// @staticGlobalFilters: list of available panels
export const getLinkedUserFilterType = (GlobalFiltersMapping: Object, globalFiltersValue: ObjModel.Obj, staticGlobalFilters: IFilterItem[]): IFiltersMetaData => {
  if (isEmpty(GlobalFiltersMapping) || isEmpty(globalFiltersValue)) return null;
  const LinkedUserType: IFiltersMetaData = {
    UserDropdownSelectType: null,
    Status: null,
  };
  const globalFilterIDs = Object.keys(GlobalFiltersMapping);
  if (isValidArrayAndNotEmpty(globalFilterIDs)) {
    globalFilterIDs.forEach((filterID) => {
      const linkedUserFilter = staticGlobalFilters.find((filter) => filter.ID === filterID && filter.Type === FilterType.UserMultiSelect);
      if (linkedUserFilter) {
        LinkedUserType.UserDropdownSelectType = linkedUserFilter?.filterData?.FilterResponse?.Type;
        LinkedUserType.Status = linkedUserFilter?.Metadata?.Status;
      }
    });
  }
  return LinkedUserType.UserDropdownSelectType ? LinkedUserType : null;
};

export const getMeasureFieldAlias = (selectedMeasure: IColumn) => (isMeasureDynamic(selectedMeasure) ? selectedMeasure.BuilderConfig?.Alias : '');

// Detect the change in filter Response
export const isFilterChanged = (staticGlobalFilters: IFilterItem[], dynamicGlobalFilters: IFilterItem[]) => {
  const staticFilters = cloneDeep(staticGlobalFilters) || [];
  const dynamicFilters = cloneDeep(dynamicGlobalFilters) || [];

  // Remove FilterOptions and Options Meta for the comparission
  if (isValidArray(staticFilters) && isValidArray(dynamicFilters)) {
    staticFilters?.forEach((filter) => {
      if (filter.filterData) {
        filter.filterData.FilterOptions = undefined;
        filter.filterData.OptionsMeta = undefined;
      }
    });
    dynamicFilters?.forEach((filter) => {
      if (filter.filterData) {
        filter.filterData.FilterOptions = undefined;
        filter.filterData.OptionsMeta = undefined;
      }
    });
    return isEqual(staticFilters, dynamicFilters);
  }
  return true;
};
// to validate if the given curDataType is same as the selectedDataType (applicable only for string and Int)
// @selectedDataType: datatype w.r.t which the validation is done
// @curDataType: datatype for which the validation is done
export const validateStringAndIntAndBoolDatatypes = (selectedDataType:string, curDataType:string) :boolean => {
  if (selectedDataType === ColumnDataTypes.String || selectedDataType === ColumnDataTypes.Boolean) {
    return selectedDataType === curDataType;
  }
  return NumericDataTypes.includes(curDataType);
};

// Update the filter value if it is a partition filter and returns the filter object with the updated value.
// @filter: dashboard filter of which filter value needs to be updated.
export const updatePartitionFilterValue = (filter: IFilterItem, partitionColumnFilterDateLimit: number): IFilterItem => {
  const newFilter = cloneDeep(filter);
  if (newFilter?.filterData?.FilterResponse?.Type === IDateRangeTypes.Relative) {
    newFilter.filterData.FilterResponse = getDatesIfRelativeRangeIsRestricted(newFilter?.filterData?.FilterResponse?.Value, partitionColumnFilterDateLimit);
  } else {
    newFilter.filterData.FilterResponse = getDatesIfAbsoluteRangeIsRestricted(moment(newFilter?.filterData?.FilterResponse?.Value?.From),
      moment(newFilter?.filterData?.FilterResponse?.Value?.To), partitionColumnFilterDateLimit);
  }
  return newFilter;
};

/**
 * Finds and returns a column object from an array of columns based on the provided value
 * @param columns - Array of IColumn objects to search through
 * @param value - Either a string (column name) or an object (containing Id)
 * @returns The matching IColumn object or undefined if no match is found
 */
export const parseAndGetColumnObj = (columns: IColumn[], value: string | ObjModel.Obj, isListReport?: boolean): IColumn => {
  let field: IColumn;
  if (isString(value)) {
    field = columns.find((column: IColumn) => column.Name === value);
  } else {
    field = columns.find((column: IColumn) => column.Id === (value as IColumn)?.Id);
  }

  if (field?.Id) return field;
  if (isListReport) {
    return null;
  }
  field = { Id: ShowErrorForWidget, Name: ShowErrorForWidget, Props: {} };
  return field;
};

export const getLSQHomeDashboardReferral = (
  isMobileApp: boolean,
  locationHash: string,
  fromMobileListing: boolean,
  fromMobileHomePage: boolean,
  isMobileStandardView: boolean,
): string | null => {
  if (!isMobileApp || !locationHash.includes(SectionItemId.LSQHomeDashboard)) return null;

  const hashRoute = locationHash.split('?')[0];
  const viewValue = isMobileStandardView ? `&view=${DashboardMobileStandardViewParam}` : '';
  let referralParam = '';
  if (fromMobileListing) referralParam = `?referral=${MobileListingURLParam}${viewValue}`;
  else if (fromMobileHomePage) referralParam = `?referral=${MobileHomePageURLParam}${viewValue}`;

  return encodeURIComponent(hashRoute + referralParam);
};

export const getAffectedPanelIds = (updatedFilters: string[], panelPayloads: any[]): string[] => {
  const panelSet = new Set<string>();
  panelPayloads.forEach((panel) => {
    const MappedFilters = Object.keys(panel?.MappingFilters || {});
    updatedFilters.forEach((item) => {
      if (MappedFilters.includes(item)) {
        panel.Target.forEach((panelId: string) => {
          panelSet.add(panelId);
        });
      }
    });
  });
  return Array.from(panelSet);
};
